/*
 * @nu-art/logger - Flexible logging infrastructure with multiple output targets
 * Copyright (C) 2026 Adam van der Kruk aka TacB0sS
 * Licensed under the Apache License, Version 2.0
 */
import { LogLevel, LogLevelOrdinal } from './types.js';
/**
 * Represents a debug flag that controls logging for a specific logger tag.
 *
 * Each DebugFlag controls two aspects of logging:
 * 1. **Enabled state**: Whether logging is active for this flag
 * 2. **Minimum log level**: The lowest log level that will be output
 *
 * DebugFlags are automatically registered with the DebugFlags singleton when created.
 * They are typically created automatically by Logger instances using the logger's tag.
 *
 * @example
 * ```typescript
 * // Created automatically by Logger
 * const logger = new Logger('MyService');
 * // logger._DEBUG_FLAG is a DebugFlag with key 'MyService'
 *
 * // Or manually
 * const flag = DebugFlags.createFlag('CustomTag', LogLevel.Debug);
 * flag.enable();
 * flag.setMinLevel(LogLevel.Warning);
 * ```
 */
export class DebugFlag {
    /** Default minimum log level for new debug flags */
    static DefaultLogLevel = LogLevel.Info;
    /** Unique identifier for this debug flag (typically matches logger tag) */
    key;
    /** Minimum log level that will be output when this flag is enabled */
    minLogLevel;
    /**
     * Creates a new DebugFlag and automatically registers it with DebugFlags.
     *
     * @param key - Unique identifier for this flag
     * @param minLogLevel - Minimum log level (defaults to DebugFlag.DefaultLogLevel)
     */
    constructor(key, minLogLevel = DebugFlag.DefaultLogLevel) {
        this.key = key;
        this.minLogLevel = minLogLevel;
        DebugFlags.add(this);
    }
    /**
     * Sets the minimum log level for this flag.
     *
     * Only log messages at or above this level will be output when the flag is enabled.
     *
     * @param minLogLevel - Minimum log level (Verbose < Debug < Info < Warning < Error)
     */
    setMinLevel(minLogLevel) {
        this.minLogLevel = minLogLevel;
    }
    /**
     * Renames this debug flag (updates the key).
     *
     * Useful when a logger's tag changes. Updates the registration in DebugFlags.
     *
     * @param newKey - New key/identifier for this flag
     */
    rename(newKey) {
        const oldKey = this.key;
        DebugFlags.rename(oldKey, newKey);
        this.key = newKey;
        // Update ActiveDebugFlags if the flag is enabled
        const activeIndex = DebugFlags.instance.ActiveDebugFlags.indexOf(oldKey);
        if (activeIndex > -1) {
            DebugFlags.instance.ActiveDebugFlags[activeIndex] = newKey;
        }
    }
    /**
     * Gets the unique key/identifier for this flag.
     */
    getKey() {
        return this.key;
    }
    /**
     * Enables or disables this debug flag.
     *
     * When disabled, all logging for this flag is suppressed regardless of log level.
     * When enabled, logging is controlled by the minimum log level.
     *
     * @param enable - If true, enables the flag. If false, disables it. Defaults to true.
     */
    enable(enable = true) {
        if (this.isEnabled() === enable)
            return;
        if (enable)
            this._enable();
        else
            this._disable();
    }
    /**
     * Checks if this debug flag is currently enabled.
     *
     * @returns true if enabled, false otherwise
     */
    isEnabled() {
        return DebugFlags.instance.ActiveDebugFlags.includes(this.key);
    }
    /**
     * Checks if a log message at the given level should be output.
     *
     * Returns true if:
     * - The flag is enabled, AND
     * - The log level is at or above the minimum log level
     *
     * @param level - Log level to check
     * @returns true if the message should be logged, false otherwise
     */
    canLog(level) {
        if (!this.isEnabled())
            return false;
        return LogLevelOrdinal.indexOf(level) >= LogLevelOrdinal.indexOf(this.minLogLevel);
    }
    /**
     * Enables this flag by adding it to the active flags list.
     */
    _enable() {
        DebugFlags.instance.ActiveDebugFlags.push(this.key);
    }
    /**
     * Disables this flag by removing it from the active flags list.
     */
    _disable() {
        const index = DebugFlags.instance.ActiveDebugFlags.indexOf(this.key);
        if (index > -1)
            DebugFlags.instance.ActiveDebugFlags.splice(index, 1);
    }
}
/**
 * Singleton manager for all debug flags in the application.
 *
 * Maintains a registry of all DebugFlag instances and tracks which flags are
 * currently active (enabled). Provides factory methods for creating flags.
 *
 * DebugFlags are used throughout the application to control logging granularity
 * - you can enable/disable logging for specific modules or services by toggling
 * their associated debug flags.
 */
export class DebugFlags {
    /** Singleton instance of DebugFlags */
    static instance = new DebugFlags();
    /** Registry of all debug flags, keyed by their identifier */
    AllDebugFlags = {};
    /** List of keys for currently enabled (active) debug flags */
    ActiveDebugFlags = [];
    /**
     * Private constructor enforces singleton pattern.
     */
    constructor() {
    }
    /**
     * Creates a new DebugFlag and registers it.
     *
     * This is the only way to create DebugFlag instances (constructor is private).
     * The flag is automatically registered in AllDebugFlags.
     *
     * @param key - Unique identifier for the flag
     * @param minLogLevel - Minimum log level (defaults to DebugFlag.DefaultLogLevel)
     * @returns The newly created DebugFlag
     */
    static createFlag(key, minLogLevel = DebugFlag.DefaultLogLevel) {
        // @ts-ignore
        return new DebugFlag(key, minLogLevel);
    }
    /**
     * Registers a debug flag in the AllDebugFlags registry.
     *
     * Called automatically when a DebugFlag is created. Should not be called directly.
     *
     * @param flag - DebugFlag to register
     */
    static add(flag) {
        this.instance.AllDebugFlags[flag.getKey()] = flag;
    }
    /**
     * Renames a debug flag by updating its key in the registry.
     *
     * Moves the flag from the old key to the new key in AllDebugFlags.
     * Also updates the flag's internal key via the flag's rename method.
     *
     * @param previousKey - Current key of the flag
     * @param newKey - New key for the flag
     */
    static rename(previousKey, newKey) {
        const flag = this.instance.AllDebugFlags[previousKey];
        if (!flag)
            return;
        delete this.instance.AllDebugFlags[previousKey];
        this.instance.AllDebugFlags[newKey] = flag;
    }
}
//# sourceMappingURL=debug-flags.js.map